// Indicadores de gestión · HU-35 (MG-ILE-68), spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-68.md`.
//
// Los ocho indicadores del procedimiento PRO-FV-001 en tarjetas, y desde cada una los casos que
// lo componen. Lee `GET /tableros/indicadores` y `GET /tableros/indicadores/{codigo}/casos`
// (AT S3 §2.2.8).
//
// CADA INDICADOR LLEGA CALCULADO (`estandares_frontend.md` §8.1 R1). El valor, la meta en texto,
// «Cumple» / «No cumple» / «Sin casos en el periodo» y su ícono los deriva el servidor; la
// pantalla no suma casos ni compara con la meta. Un periodo sin casos NO es 0 % ni 100 %
// (HU-35-C4): el valor es `null` y la tarjeta dice el texto del servidor.
//
// CC-02 (B10, AT S3 §11.3.9, `ui/MG-ILE-1076.md`): el cumplimiento del plazo regulatorio y el del
// informe final se miden POR CARTA. El filtro «Autoridad» los recorta a las cartas de esa
// autoridad (los demás indicadores no son de una autoridad) y el detalle nombra la autoridad de
// cada carta cuando el caso tiene más de una.
//
// LOS CÓDIGOS SON LOS DEL SERVIDOR (`tiempo_de_registro`, …), no los de la versión de la spec
// (`tiempo_registro`): los testids se derivan del código vigente en kebab-case
// (`indicadores-tarjeta-tiempo-de-registro`). Ver `10_codigo/local/REVISION_FINAL_SPRINT3.md`, G.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChartLine, ListChecks, RefreshCw, ShieldOff, X } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, KpiCard, Mono, PageHeader, Select, StatusBadge,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import SelectorDePais from '../components/ui/SelectorDePais';
import FiltrosColapsables from '../components/ui/FiltrosColapsables';
import Paginacion, { POR_PAGINA_DE_LISTADOS } from '../components/ui/Paginacion';
import { consultarCasosDelIndicador, consultarIndicadores } from '../api/tableros';
import type { CasoDelIndicador, CasosDelIndicador, Indicador, RecorteDelTablero, Tablero } from '../api/tableros';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { comoFecha, comoFechaYHora, esFechaSola } from '../lib/fechas';
import { filtrosIniciales, guardarFiltros } from '../lib/filtrosPersistidos';
import { useCatalogosOpcional, useZonaDelPais } from '../lib/CatalogosContexto';
import type { PaisDelFormulario } from '../lib/CatalogosContexto';
import { iconoDelServidor, tonoDelCumplimiento, tonoDelResultado, unidadDelIndicador } from '../lib/etiquetas';

/**
 * Cuántos meses ofrece el filtro de periodo, del actual hacia atrás. La spec pide «los meses con
 * casos» de `GET /indicadores/periodos`, que el contrato no tiene: se ofrecen los últimos doce.
 */
const MESES_OFRECIDOS = 12;

type Periodo = { value: string; label: string; desde: string; hasta: string };

const dosCifras = (n: number) => String(n).padStart(2, '0');

/**
 * Los meses del filtro, el actual primero: «Octubre de 2026», del 01 al último día. El nombre del
 * mes es el del español de Perú («Setiembre»), igual en todo navegador con `es-PE`.
 */
export function periodosRecientes(hoy: Date = new Date(), cuantos = MESES_OFRECIDOS): Periodo[] {
  return Array.from({ length: cuantos }, (_, i) => {
    const primero = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    const anio = primero.getFullYear();
    const mes = primero.getMonth() + 1;
    const ultimo = new Date(anio, mes, 0).getDate();
    const nombre = primero.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
    return {
      value: `${anio}-${dosCifras(mes)}`,
      label: nombre.charAt(0).toUpperCase() + nombre.slice(1),
      desde: `${anio}-${dosCifras(mes)}-01`,
      hasta: `${anio}-${dosCifras(mes)}-${dosCifras(ultimo)}`,
    };
  });
}

/** La cifra con coma decimal, como la escriben los criterios («0,5», «87,5 %»). */
const cifra = new Intl.NumberFormat('es', { maximumFractionDigits: 2 });

/**
 * `tiempo_de_registro` -> `tiempo-de-registro`: el sufijo del TESTID de la tarjeta (kebab-case,
 * `estandares_testabilidad.md` §7). No es un texto visible: lo que se lee es `indicador.nombre`.
 */
export const sufijoDe = (codigo: string): string => codigo.split('_').join('-');

/** «1 de 2» en un porcentaje (HU-35-C6); en un promedio o una razón, cuántos entran al cálculo. */
function baseDelIndicador(indicador: Indicador): string {
  if (indicador.unidad === 'porcentaje' && indicador.numerador !== null) {
    return `${cifra.format(indicador.numerador)} de ${indicador.denominador}`;
  }
  return `${indicador.denominador} en el cálculo`;
}

/** Una tarjeta: valor, meta, si se cumple (texto e ícono, no solo color), fórmula y base. */
function TarjetaDeIndicador({ indicador, onVerCasos }: { indicador: Indicador; onVerCasos: (codigo: string) => void }) {
  const t = `indicadores-tarjeta-${sufijoDe(indicador.codigo)}`;
  const valor = indicador.hay_datos && indicador.valor !== null
    ? <span data-testid={`${t}-valor`}>{cifra.format(indicador.valor)}</span>
    // HU-35-C4: sin casos, el texto del servidor y nunca una cifra.
    : <span data-testid={`${t}-valor`} className="text-body">{indicador.texto_de_cumplimiento}</span>;
  return (
    <KpiCard
      data-testid={t}
      label={indicador.nombre}
      value={valor}
      unit={indicador.hay_datos ? unidadDelIndicador(indicador.unidad) : undefined}
      meta={(
        <span className="flex flex-col gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <span data-testid={`${t}-meta`} className="text-meta text-ink">Meta: {indicador.texto_de_meta}</span>
            <StatusBadge data-testid={`${t}-cumple`} tone={tonoDelCumplimiento(indicador.cumple_la_meta)}
              icon={iconoDelServidor(indicador.icono)}>
              {indicador.texto_de_cumplimiento}
            </StatusBadge>
          </span>
          <span data-testid={`${t}-formula`} className="text-meta text-ink-soft">{indicador.formula}</span>
          <span className="flex flex-wrap items-center justify-between gap-2">
            {indicador.hay_datos && (
              <span data-testid={`${t}-base`} className="text-meta text-ink-soft">{baseDelIndicador(indicador)}</span>
            )}
            {indicador.hay_datos && (
              <Button size="sm" variant="ghost" icon={ListChecks} data-testid={`${t}-ver-casos`}
                aria-label={`Ver los casos de ${indicador.nombre}`} onClick={() => onVerCasos(indicador.codigo)}>
                Ver casos
              </Button>
            )}
          </span>
        </span>
      )}
    />
  );
}

/** Las fechas que explican un caso del indicador, con la etiqueta que da el servidor. */
function FechasDelCaso({ caso }: { caso: CasoDelIndicador }) {
  // RN-82: las horas del caso, en la zona de su país (`pais` llega con cada caso del indicador).
  const zona = useZonaDelPais()(caso.pais);
  return (
    <span className="flex flex-col text-meta text-ink-soft">
      {caso.fechas.map((f) => (
        <span key={f.etiqueta} className="whitespace-nowrap">
          {f.etiqueta}: <Mono className="text-ink">{esFechaSola(f.valor) ? comoFecha(f.valor) : comoFechaYHora(f.valor, zona)}</Mono>
        </span>
      ))}
    </span>
  );
}

const COLUMNAS_DEL_DETALLE: ColumnaDeTabla[] = [
  { key: 'caso', label: 'Caso' },
  { key: 'situacion', label: 'Situación', testid: 'indicadores-detalle-fila-situacion' },
  { key: 'fechas', label: 'Fechas', testid: 'indicadores-detalle-fila-fechas' },
];

/** HU-35-C6: el panel con los casos de UN indicador, las mismas mediciones que la tarjeta. */
function DetalleDelIndicador({ codigo, recorte, onCerrar, onAbrirCaso }: {
  codigo: string;
  recorte: RecorteDelTablero;
  onCerrar: () => void;
  onAbrirCaso?: (casoId: string) => void;
}) {
  const [detalle, setDetalle] = useState<CasosDelIndicador | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargandoPagina, setCargandoPagina] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const { desde, hasta, pais, autoridad } = recorte;

  // Paginación del SERVIDOR, de 10 en 10 (operador, 5-oct-2026). La página va atada al
  // indicador y al recorte: otro indicador, periodo o país vuelve a la página 1 sin una
  // consulta de más por la página vieja.
  const clave = [codigo, desde, hasta, pais ?? ''].join('|');
  const [paginaPedida, setPaginaPedida] = useState({ clave, pagina: 1 });
  const pagina = paginaPedida.clave === clave ? paginaPedida.pagina : 1;
  const claveCargada = useRef<string | null>(null);

  useEffect(() => {
    const control = new AbortController();
    // Otro indicador o recorte vacía el panel; otra página del mismo conserva las filas hasta
    // que llegan las nuevas, y el foco sigue en «Siguiente».
    if (claveCargada.current !== clave) setDetalle(null);
    setFallo(null);
    setCargandoPagina(true);
    consultarCasosDelIndicador(codigo, { desde, hasta, pais, autoridad },
      { pagina, porPagina: POR_PAGINA_DE_LISTADOS, senal: control.signal })
      .then((respuesta) => {
        if (control.signal.aborted) return;
        claveCargada.current = clave;
        setDetalle(respuesta);
      })
      .catch((error: unknown) => { if (!control.signal.aborted) setFallo(aFallo(error)); })
      .finally(() => { if (!control.signal.aborted) setCargandoPagina(false); });
    return () => control.abort();
  }, [clave, codigo, desde, hasta, pais, autoridad, pagina]);

  // El panel se abre a un lado: el foco va a él para que el lector lo anuncie y Tab siga desde ahí.
  useEffect(() => { panel.current?.focus(); }, [codigo]);

  // CC-02: un caso con dos cartas aporta dos filas; la clave es el caso y su autoridad.
  const filas: FilaDeTabla[] = (detalle?.casos ?? []).map((c) => ({ ...c, id: `${c.caso_id}:${c.autoridad_codigo ?? ''}` }));
  const celda = (fila: FilaDeTabla, { key }: ColumnaDeTabla) => {
    const caso = fila as unknown as CasoDelIndicador;
    if (key === 'caso') {
      return (
        <span className="inline-flex flex-col items-start">
          <Button size="sm" variant="ghost" data-testid="indicadores-detalle-fila-caso"
            aria-label={`Abrir el expediente de ${caso.numero ?? 'el caso'}`} onClick={() => onAbrirCaso?.(caso.caso_id)}>
            <Mono className="whitespace-nowrap">{caso.numero ?? 'Caso sin número'}</Mono>
          </Button>
          {caso.autoridad && (
            <span className="text-meta text-ink-soft pl-2" data-testid="indicadores-detalle-fila-autoridad">{caso.autoridad}</span>
          )}
        </span>
      );
    }
    if (key === 'situacion') {
      return (
        <span className="inline-flex flex-col gap-0.5">
          <StatusBadge tone={tonoDelResultado(caso.resultado)}>{caso.texto_del_resultado}</StatusBadge>
          {caso.detalle && <span className="text-meta text-ink-soft">{caso.detalle}</span>}
        </span>
      );
    }
    return <FechasDelCaso caso={caso} />;
  };

  return (
    <div ref={panel} tabIndex={-1} className="destino-de-foco outline-none" aria-label="Casos del indicador">
      <Card data-testid="indicadores-detalle" title={detalle?.indicador.nombre ?? 'Casos del indicador'}
        subtitle={detalle ? `Del ${comoFecha(detalle.desde)} al ${comoFecha(detalle.hasta)}` : undefined}
        actions={(
          <Button size="sm" variant="ghost" icon={X} data-testid="indicadores-detalle-cerrar" onClick={onCerrar}>
            Cerrar
          </Button>
        )}
        pad={false}>
        {!detalle && !fallo && <p role="status" className="p-5 text-body text-ink-soft">Cargando los casos del indicador…</p>}
        {fallo && (
          <div className="p-4">
            <Alert tone="danger" title="No se pudieron cargar los casos del indicador" detalleTecnico={fallo.detalleTecnico}>
              {fallo.message}
            </Alert>
          </div>
        )}
        {detalle && detalle.indicador.limitaciones.length > 0 && (
          <ul className="px-5 pt-4 text-meta text-ink-soft list-disc list-inside">
            {detalle.indicador.limitaciones.map((l) => <li key={l}>{l}</li>)}
          </ul>
        )}
        {detalle && (filas.length
          ? <DataTable columns={COLUMNAS_DEL_DETALLE} rows={filas} renderCell={celda} testidDeFila="indicadores-detalle-fila" sinMarco />
          : <p className="p-5 text-body text-ink-soft">Ningún caso compone este indicador en el periodo.</p>)}
        {detalle && detalle.total > 0 && (
          <div className="px-5 py-3 border-t border-hairline" aria-busy={cargandoPagina}>
            <Paginacion pagina={detalle.pagina} total={detalle.total} enEstaPagina={filas.length}
              porPagina={detalle.por_pagina || POR_PAGINA_DE_LISTADOS}
              onPagina={(p) => setPaginaPedida({ clave, pagina: p })}
              etiqueta="Paginación de los casos del indicador" sustantivo={['caso', 'casos']}
              data-testid="indicadores-paginacion" />
          </div>
        )}
      </Card>
    </div>
  );
}

type FiltrosDelTablero = { periodo: string; pais: string; autoridad: string; detalle: string };
const SIN_FILTROS: FiltrosDelTablero = { periodo: '', pais: '', autoridad: '', detalle: '' };

/**
 * CC-02 (B10): las autoridades que ofrece el filtro: las del país elegido o, sin país, las de
 * todos los países del catálogo, sin repetir código. Vienen del catálogo, no se inventan.
 */
const autoridadesDelFiltro = (paises: PaisDelFormulario[], pais: string): { value: string; label: string }[] => {
  const vistas = new Set<string>();
  return paises
    .filter((p) => !pais || p.cod === pais)
    .flatMap((p) => p.autoridades ?? [])
    .filter((a) => (vistas.has(a.codigo) ? false : (vistas.add(a.codigo), true)))
    .map((a) => ({ value: a.codigo, label: a.nombre }));
};

type ClaseDeFallo = 'envio' | 'permiso' | 'carga';

/** Un 403 o 422 con país es el país fuera del ámbito (HU-35-C3); sin país, el rol no entra. */
const claseDeFallo = (fallo: Fallo, conPais: boolean): ClaseDeFallo => {
  if ((fallo.estado === 403 || fallo.estado === 422) && conPais) return 'envio';
  if (fallo.estado === 403) return 'permiso';
  return 'carga';
};

export default function Indicadores({ onAbrirCaso, onSalir, hoy }: {
  /** Abre el expediente del caso; «Volver» regresa aquí con el periodo y el país. */
  onAbrirCaso?: (casoId: string) => void;
  /** La salida del estado «sin permiso»: el módulo de inicio del rol. */
  onSalir?: () => void;
  /** Solo para pruebas: el día con que se arma la lista de periodos. */
  hoy?: Date;
}) {
  const catalogos = useCatalogosOpcional();
  const [periodos] = useState(() => periodosRecientes(hoy));
  const [filtros, setFiltros] = useState<FiltrosDelTablero>(() => {
    const guardados = filtrosIniciales('indicadores', SIN_FILTROS);
    // Un periodo guardado que ya no se ofrece (cambió el mes) vuelve al actual.
    return periodos.some((p) => p.value === guardados.periodo) ? guardados : { ...guardados, periodo: periodos[0]?.value ?? '' };
  });
  const [tablero, setTablero] = useState<Tablero | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<{ fallo: Fallo; clase: ClaseDeFallo } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const enCurso = useRef<AbortController | null>(null);

  useEffect(() => { guardarFiltros('indicadores', filtros); }, [filtros]);

  const periodo = periodos.find((p) => p.value === filtros.periodo) ?? periodos[0];
  const recorte: RecorteDelTablero = {
    desde: periodo?.desde ?? '', hasta: periodo?.hasta ?? '', pais: filtros.pais || undefined, autoridad: filtros.autoridad || undefined,
  };
  const { desde, hasta, pais, autoridad } = recorte;
  // Lo que el usuario cambió respecto de lo de entrada: el periodo siempre tiene valor, así que
  // cuenta solo si no es el mes en curso (el de por defecto); el país y la autoridad cuentan si hay uno elegido.
  const filtrosActivos = Number(Boolean(periodo) && periodo !== periodos[0]) + Number(Boolean(filtros.pais))
    + Number(Boolean(filtros.autoridad));

  const cargar = useCallback(async () => {
    enCurso.current?.abort();
    const control = new AbortController();
    enCurso.current = control;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarIndicadores({ desde, hasta, pais, autoridad }, { senal: control.signal });
      if (!control.signal.aborted) setTablero(respuesta);
    } catch (error) {
      if (control.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      const normalizado = aFallo(error);
      setFallo({ fallo: normalizado, clase: claseDeFallo(normalizado, Boolean(pais)) });
    } finally {
      if (!control.signal.aborted) setCargando(false);
    }
  }, [desde, hasta, pais, autoridad]);

  useEffect(() => { cargar(); }, [cargar, recarga]);
  useEffect(() => () => enCurso.current?.abort(), []);

  const cambiar = (cambio: Partial<FiltrosDelTablero>) => setFiltros((f) => ({ ...f, ...cambio }));

  const paises = catalogos?.PAISES ?? [];
  const autoridades = autoridadesDelFiltro(paises, filtros.pais);
  const sinPermiso = fallo?.clase === 'permiso';
  const indicadores = tablero?.indicadores ?? [];
  const todosSinDatos = indicadores.length > 0 && indicadores.every((i) => !i.hay_datos);
  const conDetalle = Boolean(filtros.detalle) && Boolean(tablero) && !cargando && fallo?.clase !== 'carga';

  if (sinPermiso && fallo) {
    return (
      <EmptyState principal data-testid="indicadores-sin-permiso" icon={ShieldOff}
        title="No tienes acceso a los indicadores" description={fallo.fallo.message}
        action={onSalir ? <Button onClick={onSalir}>Ir a mi pantalla de inicio</Button> : null} />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Gestión · Indicadores"
        hu="HU-35 · MG-ILE-68"
        title="Indicadores de gestión"
        subtitle="Los ocho indicadores del procedimiento PRO-FV-001, calculados sobre los casos de tu ámbito."
        icon={ChartLine}
        actions={(
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => setRecarga((n) => n + 1)} disabled={cargando}>
            Refrescar
          </Button>
        )}
      />

      {/* Plegable y abierta al entrar (operador, 5-oct-2026); plegada dice cuántos hay puestos. */}
      <FiltrosColapsables className="mb-4" data-testid="indicadores-filtros" activos={filtrosActivos}>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Periodo">
            <Select data-testid="indicadores-filtro-periodo" compacto sinVacio value={filtros.periodo}
              onChange={(e) => cambiar({ periodo: String(e.target.value ?? ''), detalle: '' })}
              options={periodos.map((p) => ({ value: p.value, label: p.label }))} />
          </Field>
          <Field label="País">
            {/* Con la bandera de cada país, como todo selector de país (operador, 5-oct-2026). */}
            <SelectorDePais data-testid="indicadores-filtro-pais" compacto value={filtros.pais} placeholder="Todos los países"
              onChange={(e) => cambiar({ pais: String(e.target.value ?? ''), autoridad: '', detalle: '' })}
              paises={paises} />
          </Field>
          {/* CC-02 (§11.3.9): recorta los indicadores que se miden por carta. */}
          <Field label="Autoridad" hint="Se aplica al plazo regulatorio y al informe final, que se miden por carta.">
            <Select data-testid="indicadores-filtro-autoridad" compacto value={filtros.autoridad ?? ''} placeholder="Todas las autoridades"
              onChange={(e) => cambiar({ autoridad: String(e.target.value ?? ''), detalle: '' })}
              options={autoridades} />
          </Field>
        </div>
      </FiltrosColapsables>

      {fallo?.clase === 'envio' && (
        <div className="mb-4">
          <Alert data-testid="indicadores-error-envio" tone="danger" title="No se pudo aplicar el filtro"
            detalleTecnico={fallo.fallo.detalleTecnico}>
            {fallo.fallo.message}
          </Alert>
        </div>
      )}
      {fallo?.clase === 'carga' && (
        <div className="mb-4">
          <Alert data-testid="indicadores-error" tone="danger" title="No se pudieron calcular los indicadores"
            detalleTecnico={fallo.fallo.detalleTecnico}>
            {fallo.fallo.message}
            {fallo.fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
            <span className="block mt-2">
              <Button size="sm" variant="secondary" icon={RefreshCw} onClick={() => setRecarga((n) => n + 1)}>Reintentar</Button>
            </span>
          </Alert>
        </div>
      )}

      {cargando && (
        <div data-testid="indicadores-cargando" role="status" aria-label="Calculando los indicadores"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="h-40 rounded-2xl border border-borde bg-white" />
          ))}
        </div>
      )}

      {!cargando && tablero && fallo?.clase !== 'carga' && (
        <>
          <p className="text-meta text-ink-soft mb-3">
            Periodo del <Mono className="text-ink">{comoFecha(tablero.desde)}</Mono> al{' '}
            <Mono className="text-ink">{comoFecha(tablero.hasta)}</Mono>. Solo agregados: ningún dato de la sección de personas.
          </p>
          <div className={conDetalle ? 'grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_26rem] items-start' : ''}>
            <div data-testid={todosSinDatos ? 'indicadores-vacio' : 'indicadores-exito'}
              className={'grid grid-cols-1 gap-4 sm:grid-cols-2 ' + (conDetalle ? '' : 'xl:grid-cols-4')}>
              {tablero.indicadores.map((i) => (
                <TarjetaDeIndicador key={i.codigo} indicador={i} onVerCasos={(codigo) => cambiar({ detalle: codigo })} />
              ))}
            </div>
            {conDetalle && (
              <DetalleDelIndicador codigo={filtros.detalle} recorte={recorte}
                onCerrar={() => cambiar({ detalle: '' })} onAbrirCaso={onAbrirCaso} />
            )}
          </div>
        </>
      )}
    </>
  );
}
